import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { LeadForm } from "@/components/common/LeadForm";
import { SEO_SERVICES } from "@/data/seoServices";
import { AREA_DETAILS } from "@/data/seoServices";
import { AR_SERVICES, AR_AREAS } from "@/data/seoServicesAr";
import { AR_SERVICES_FULL } from "@/data/seoServicesArFull";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Timer,
  Star,
  MapPin,
  HelpCircle,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServiceLanding() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const base = SEO_SERVICES[slug];
  if (!base) return <Navigate to="/services" replace />;
  // Merge Arabic overlay (key SEO fields + full lists/FAQs)
  const arKey = lang === "ar" ? AR_SERVICES[slug] || {} : {};
  const arFull = lang === "ar" ? AR_SERVICES_FULL[slug] || {} : {};
  const data = { ...base, ...arKey, ...arFull };

  // Page-local UI labels (kept inline — page-specific, not reused elsewhere)
  const L = lang === "ar" ? {
    ctaQuote: "احصل على عرض سعر مجاني",
    keyBenefits: "الفوائد الرئيسية",
    whyChooseOrion: "لماذا يختار المشغلون أوريون",
    whatIs: "ما هو؟",
    whoFor: "لمن؟",
    scope: "نطاق العمل",
    whatsIncluded: "ما المشمول.",
    pricing: "دليل التسعير",
    whyOrion: "لماذا أوريون",
    frequentlyAsked: "أسئلة شائعة",
    answersFrom: "إجابات من فريق أوريون.",
    gotSomething: "هل لديك سؤال محدد؟ ",
    waOurTeam: "راسلنا على WhatsApp",
    replyMinutes: " — نرد خلال دقائق.",
    coverageAreas: "مناطق التغطية",
    weServe: "نخدم العملاء في:",
    quoteReply: "نرد خلال ساعة عمل واحدة.",
    quoteSub: "أخبرنا عن موقعك ومتطلباتك. سيتصل بك مستشار كبير ويعد عرض سعر مخصصاً — بدون التزام.",
    freeSiteVisit: "زيارة موقع مجانية للأعمال المؤهلة",
    fixedPricing: "أسعار ثابتة، بدون مفاجآت",
    sameDay: "نشر فوري متاح",
    waMsgPrefix: "مرحباً أوريون — أحتاج",
    waMsgSuffix: "في الإمارات.",
  } : {
    ctaQuote: "Get a Free Quote",
    keyBenefits: "Key Benefits",
    whyChooseOrion: "Why operators choose Orion",
    whatIs: "What is it?",
    whoFor: "Who is it for?",
    scope: "Scope of work",
    whatsIncluded: "What\u2019s included.",
    pricing: "Pricing guide",
    whyOrion: "Why Orion",
    frequentlyAsked: "Frequently asked",
    answersFrom: "Answers from the Orion team.",
    gotSomething: "Got something specific? ",
    waOurTeam: "WhatsApp our team",
    replyMinutes: " — we reply in minutes.",
    coverageAreas: "Coverage areas",
    weServe: "We serve clients in:",
    quoteReply: "Reply within 1 business hour.",
    quoteSub: "Tell us about your site and what you need. A senior consultant will call you back and prepare a tailored quote — no obligation.",
    freeSiteVisit: "Free site visit for qualifying jobs",
    fixedPricing: "Fixed pricing, no surprise extras",
    sameDay: "Same-day mobilization available",
    waMsgPrefix: "Hi Orion — I need",
    waMsgSuffix: "in UAE.",
  };

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.h1,
    provider: {
      "@type": "LocalBusiness",
      name: COMPANY.brandFull,
      telephone: COMPANY.phone,
      email: COMPANY.email,
      url: `https://${COMPANY.domain}`,
    },
    areaServed: ["Dubai", "Abu Dhabi", "Sharjah", "Ajman"],
    description: data.metaDesc,
    serviceType: data.h1,
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`service-landing-${slug}`}>
      <SEO title={data.metaTitle} description={data.metaDesc} jsonLd={serviceLd} />
      <SEO jsonLd={faqLd} jsonLdId={`faq-${slug}-jsonld`} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-30">
          <img src={data.image} alt={data.h1} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="container-orion relative py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              {data.eyebrow}
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              {data.h1}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{data.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#quote"
                data-testid={`service-${slug}-cta-quote`}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              >
                {L.ctaQuote}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(`${L.waMsgPrefix} ${data.h1} ${L.waMsgSuffix}`)}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`service-${slug}-cta-wa`}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              >
                {t("common.whatsapp")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AI-search semantic blocks */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <div className="flex items-center gap-2 text-eyebrow text-blue-700">
              <HelpCircle className="w-4 h-4" />
              {L.whatIs}
            </div>
            <p className="mt-4 text-slate-700 leading-relaxed">{data.whatItIs}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <div className="flex items-center gap-2 text-eyebrow text-blue-700">
              <Star className="w-4 h-4" />
              {L.whoFor}
            </div>
            <p className="mt-4 text-slate-700 leading-relaxed">{data.whoItIsFor}</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">{L.keyBenefits}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {L.whyChooseOrion}.
            </h2>
          </div>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {data.benefits.map((b, i) => (
              <li
                key={i}
                data-testid={`service-${slug}-benefit-${i}`}
                className="bg-white border border-slate-200 rounded-2xl p-7 card-hover flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <span className="text-slate-700 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Scope + Pricing + Why Us */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-eyebrow">{L.scope}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              {L.whatsIncluded}
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {data.scope.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-7">
              <div className="flex items-center gap-2 text-eyebrow text-blue-700">
                <Timer className="w-4 h-4" />
                {L.pricing}
              </div>
              <p className="mt-3 text-slate-700 leading-relaxed">{data.pricing}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-7">
              <div className="flex items-center gap-2 text-eyebrow text-blue-700">
                <ShieldCheck className="w-4 h-4" />
                {L.whyOrion}
              </div>
              <ul className="mt-4 space-y-3">
                {data.whyUs.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-eyebrow">{L.frequentlyAsked}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              {L.answersFrom}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              {L.gotSomething}
              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-700 underline hover:no-underline"
              >
                {L.waOurTeam}
              </a>
              {L.replyMinutes}
            </p>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid={`faq-${slug}-accordion`}>
              {data.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                  <AccordionTrigger
                    data-testid={`faq-${slug}-trigger-${i}`}
                    className="text-left font-heading text-lg font-medium text-slate-900 hover:text-blue-700"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Area links (internal linking for SEO) */}
      {data.relatedAreas && data.relatedAreas.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container-orion">
            <div className="text-eyebrow">{L.coverageAreas}</div>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
              {L.weServe}
            </h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {data.relatedAreas.map((a) => {
                const area = AREA_DETAILS[a];
                if (!area) return null;
                const areaName = lang === "ar" && AR_AREAS[a] ? AR_AREAS[a].name : area.name;
                return (
                  <Link
                    key={a}
                    to={`/areas/${a}`}
                    data-testid={`service-${slug}-area-${a}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-sm font-medium text-slate-700 transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    {areaName}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lead form */}
      <section id="quote" className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">{L.ctaQuote}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {L.quoteReply}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              {L.quoteSub}
            </p>
            <div className="mt-8 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                {L.freeSiteVisit}
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                {L.fixedPricing}
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                {L.sameDay}
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource={`service-${slug}`} defaultService={slug} simple />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
